/**
 * External dependencies
 */
import clsx from 'clsx';

/**
 * WordPress dependencies
 */
import {
	RangeControl,
	SelectControl,
	ToggleControl,
	ToolsPanel,
	ToolsPanelItem,
} from '@safe-wordpress/components';
import { _x } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import { CustomSelectControl, UnitWithRangeControl } from './internal';
import type { ToolsPanelProps } from './types';
import {
	getPreviewValue,
	hasInheritedValue,
	hasUserValue,
} from '../../../../utils/props';

export function DisplayPanel( {
	value: props,
	onChange,
}: ToolsPanelProps ): JSX.Element {
	// Display
	const displayOptions = [
		{
			value: 'block',
			label: _x( 'Block', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'contents',
			label: _x( 'Contents', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'flex',
			label: _x( 'Flex', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'grid',
			label: _x( 'Grid', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'inline',
			label: _x( 'Inline', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'inline-block',
			label: _x( 'Inline Block', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'inline-flex',
			label: _x( 'Inline Flex', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'inline-grid',
			label: _x( 'Inline Grid', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'inline-table',
			label: _x( 'Inline Table', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'table',
			label: _x( 'Table', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'table-cell',
			label: _x( 'Table Cell', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'none',
			label: _x( 'None', 'text', 'nelio-ab-testing' ),
		},
	];

	const flexDirectionOptions = [
		{
			value: 'row',
			label: _x( 'Horizontal', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'column',
			label: _x( 'Vertical', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'row-reverse',
			label: _x( 'Horizontal Reverse', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'column-reverse',
			label: _x( 'Vertical Reverse', 'text', 'nelio-ab-testing' ),
		},
	];

	const justifyContentOptions = [
		{
			value: 'normal',
			label: _x( 'Normal', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'flex-start',
			label: _x( 'Start', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'flex-end',
			label: _x( 'End', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'center',
			label: _x( 'Center', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'space-between',
			label: _x( 'Space Between', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'space-around',
			label: _x( 'Space Around', 'text', 'nelio-ab-testing' ),
		},
	];

	const alignItemsOptions = [
		{
			value: 'normal',
			label: _x( 'Normal', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'flex-start',
			label: _x( 'Start', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'flex-end',
			label: _x( 'End', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'center',
			label: _x( 'Center', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'baseline',
			label: _x( 'Baseline', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'stretch',
			label: _x( 'Stretch', 'text', 'nelio-ab-testing' ),
		},
	];

	const alignContentOptions = [
		{
			value: 'normal',
			label: _x( 'Normal', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'flex-start',
			label: _x( 'Start', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'flex-end',
			label: _x( 'End', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'center',
			label: _x( 'Center', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'space-between',
			label: _x( 'Space Between', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'space-around',
			label: _x( 'Space Around', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'stretch',
			label: _x( 'Stretch', 'text', 'nelio-ab-testing' ),
		},
	];

	const flexWrapOptions = [
		{
			value: 'nowrap',
			label: _x( 'No Wrap', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'wrap',
			label: _x( 'Wrap', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'wrap-reverse',
			label: _x( 'Wrap Reverse', 'text', 'nelio-ab-testing' ),
		},
	];

	// Float
	const floatOptions = [
		{
			value: 'none',
			label: _x( 'None', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'left',
			label: _x( 'Left', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'right',
			label: _x( 'Right', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'inline-start',
			label: _x( 'Inline Start', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'inline-end',
			label: _x( 'Inline End', 'text', 'nelio-ab-testing' ),
		},
	];

	// Clear
	const clearOptions = [
		{
			value: 'none',
			label: _x( 'None', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'left',
			label: _x( 'Left', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'right',
			label: _x( 'Right', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'both',
			label: _x( 'Both', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'inline-start',
			label: _x( 'Inline Start', 'text', 'nelio-ab-testing' ),
		},
		{
			value: 'inline-end',
			label: _x( 'Inline End', 'text', 'nelio-ab-testing' ),
		},
	];

	// Opacity
	const rawOpacityValue = getPreviewValue( props.opacity ) || '1';
	const parsedOpacityValue = isNaN( parseFloat( rawOpacityValue ) )
		? 1
		: parseFloat( rawOpacityValue );
	const constrainedOpacityValue =
		parsedOpacityValue / 100 > 1 ? 1 : parsedOpacityValue;
	const opacityValue =
		constrainedOpacityValue > 1
			? parseFloat( ( constrainedOpacityValue / 100 ).toFixed( 2 ) )
			: constrainedOpacityValue;

	// Cursor
	const cursorOptions = [
		{
			key: '',
			name: _x( 'Default', 'text', 'nelio-ab-testing' ),
		},
		{
			key: 'auto',
			name: _x( 'Auto', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'auto' },
		},
		{
			key: 'alias',
			name: _x( 'Alias', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'alias' },
		},
		{
			key: 'all-scroll',
			name: _x( 'All Scroll', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'all-scroll' },
		},
		{
			key: 'cell',
			name: _x( 'Cell', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'cell' },
		},
		{
			key: 'col-resize',
			name: _x( 'Column Resize', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'col-resize' },
		},
		{
			key: 'context-menu',
			name: _x( 'Context Menu', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'context-menu' },
		},
		{
			key: 'copy',
			name: _x( 'Copy', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'copy' },
		},
		{
			key: 'crosshair',
			name: _x( 'Cross', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'crosshair' },
		},
		{
			key: 'grab',
			name: _x( 'Grab', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'grab' },
		},
		{
			key: 'grabbing',
			name: _x( 'Grabbing', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'grabbing' },
		},
		{
			key: 'help',
			name: _x( 'Help', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'help' },
		},
		{
			key: 'move',
			name: _x( 'Move', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'move' },
		},
		{
			key: 'no-drop',
			name: _x( 'No Drop', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'no-drop' },
		},
		{
			key: 'none',
			name: _x( 'None', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'none' },
		},
		{
			key: 'not-allowed',
			name: _x( 'Not Allowed', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'not-allowed' },
		},
		{
			key: 'pointer',
			name: _x( 'Pointer', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'pointer' },
		},
		{
			key: 'progress',
			name: _x( 'Progress', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'progress' },
		},
		{
			key: 'row-resize',
			name: _x( 'Row Resize', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'row-resize' },
		},
		{
			key: 'text',
			name: _x( 'Text', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'text' },
		},
		{
			key: 'vertical-text',
			name: _x( 'Vertical Text', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'vertical-text' },
		},
		{
			key: 'wait',
			name: _x( 'Wait', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'wait' },
		},
		{
			key: 'zoom-in',
			name: _x( 'Zoom In', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'zoom-in' },
		},
		{
			key: 'zoom-out',
			name: _x( 'Zoom Out', 'text', 'nelio-ab-testing' ),
			style: { cursor: 'zoom-out' },
		},
	];
	const selectedCursorValue =
		cursorOptions.find(
			( option ) => option.key === props.cursor?.value
		) || cursorOptions[ 0 ];

	const resetAll = () =>
		onChange( {
			display: undefined,
			flexDirection: undefined,
			flexWrap: undefined,
			justifyContent: undefined,
			alignItems: undefined,
			alignContent: undefined,
			float: undefined,
			clear: undefined,
			visibility: undefined,
			opacity: undefined,
			cursor: undefined,
			pointerEvents: undefined,
		} );

	return (
		<ToolsPanel
			label={ _x( 'Display', 'text', 'nelio-ab-testing' ) }
			resetAll={ resetAll }
		>
			<ToolsPanelItem
				label={ _x( 'Display', 'text', 'nelio-ab-testing' ) }
				hasValue={ () => hasUserValue( props.display ) }
				onSelect={ () =>
					onChange( {
						display: 'block',
					} )
				}
				onDeselect={ () =>
					onChange( {
						display: undefined,
						flexDirection: undefined,
						flexWrap: undefined,
						justifyContent: undefined,
						alignItems: undefined,
						alignContent: undefined,
						columnGap: undefined,
						rowGap: undefined,
					} )
				}
				isShownByDefault={ hasInheritedValue( props.display ) }
			>
				<SelectControl
					disabled={ 'readonly' === props.display?.type }
					label={ _x( 'Display', 'text', 'nelio-ab-testing' ) }
					value={ getPreviewValue( props.display ) }
					onChange={ ( selectedValue ) =>
						onChange( {
							display: selectedValue,
							...( [ 'grid', 'inline-grid' ].includes(
								selectedValue
							) &&
								selectedValue !==
									getPreviewValue( props.display ) && {
									columnGap: '0px',
									rowGap: '0px',
								} ),
							...( ! [ 'grid', 'inline-grid' ].includes(
								selectedValue
							) && {
								columnGap: undefined,
								rowGap: undefined,
							} ),
						} )
					}
					options={ displayOptions }
				/>
				{ [ 'flex', 'inline-flex' ].includes(
					getPreviewValue( props.display )
				) && (
					<div className="nab-components-flex-control">
						<div className="nab-components-flex-control__title">
							{ _x(
								'Flex Container',
								'text',
								'nelio-ab-testing'
							) }
						</div>
						<div className="nab-components-flex-control__wrapper">
							<SelectControl
								disabled={
									'readonly' === props.flexDirection?.type
								}
								label={ _x(
									'Direction',
									'text',
									'nelio-ab-testing'
								) }
								value={ getPreviewValue( props.flexDirection ) }
								onChange={ ( selectedValue ) =>
									onChange( { flexDirection: selectedValue } )
								}
								options={ flexDirectionOptions }
							/>
							<SelectControl
								disabled={
									'readonly' === props.justifyContent?.type
								}
								label={ _x(
									'Justify Content',
									'text',
									'nelio-ab-testing'
								) }
								value={ getPreviewValue(
									props.justifyContent
								) }
								onChange={ ( selectedValue ) =>
									onChange( {
										justifyContent: selectedValue,
									} )
								}
								options={ justifyContentOptions }
							/>
							<SelectControl
								disabled={
									'readonly' === props.alignItems?.type
								}
								label={ _x(
									'Align Items',
									'text',
									'nelio-ab-testing'
								) }
								value={ getPreviewValue( props.alignItems ) }
								onChange={ ( selectedValue ) =>
									onChange( {
										alignItems: selectedValue,
									} )
								}
								options={ alignItemsOptions }
							/>
							<SelectControl
								disabled={
									'readonly' === props.alignContent?.type
								}
								label={ _x(
									'Align Content',
									'text',
									'nelio-ab-testing'
								) }
								value={ getPreviewValue( props.alignContent ) }
								onChange={ ( selectedValue ) =>
									onChange( {
										alignContent: selectedValue,
									} )
								}
								options={ alignContentOptions }
							/>
							<SelectControl
								disabled={ 'readonly' === props.flexWrap?.type }
								label={ _x(
									'Children',
									'text',
									'nelio-ab-testing'
								) }
								value={ getPreviewValue( props.flexWrap ) }
								onChange={ ( selectedValue ) =>
									onChange( {
										flexWrap: selectedValue,
									} )
								}
								options={ flexWrapOptions }
							/>
						</div>
					</div>
				) }
				{ [ 'grid', 'inline-grid' ].includes(
					getPreviewValue( props.display )
				) && (
					<div className="nab-components-grid-control">
						<div className="nab-components-grid-control__title">
							{ _x(
								'Grid Container',
								'text',
								'nelio-ab-testing'
							) }
						</div>
						<div className="nab-components-grid-control__wrapper">
							<SelectControl
								disabled={
									'readonly' === props.justifyContent?.type
								}
								label={ _x(
									'Justify Content',
									'text',
									'nelio-ab-testing'
								) }
								value={ getPreviewValue(
									props.justifyContent
								) }
								onChange={ ( selectedValue ) =>
									onChange( {
										justifyContent: selectedValue,
									} )
								}
								options={ justifyContentOptions }
							/>
							<SelectControl
								disabled={
									'readonly' === props.alignContent?.type
								}
								label={ _x(
									'Align Content',
									'text',
									'nelio-ab-testing'
								) }
								value={ getPreviewValue( props.alignContent ) }
								onChange={ ( selectedValue ) =>
									onChange( {
										alignContent: selectedValue,
									} )
								}
								options={ alignContentOptions }
							/>
							<UnitWithRangeControl
								label={ _x(
									'Column Gap',
									'text',
									'nelio-ab-testing'
								) }
								units={ [ 'px', 'em', 'rem', '%' ] }
								value={
									getPreviewValue( props.columnGap ) ?? '0px'
								}
								onChange={ ( selectedValue ) =>
									onChange( {
										columnGap: `${ selectedValue }`,
									} )
								}
								initialPosition={ 0 }
								disabled={
									props.columnGap?.type === 'readonly'
								}
								__next40pxDefaultSize
							/>
							<UnitWithRangeControl
								label={ _x(
									'Row Gap',
									'text',
									'nelio-ab-testing'
								) }
								units={ [ 'px', 'em', 'rem', '%' ] }
								value={
									getPreviewValue( props.rowGap ) ?? '0px'
								}
								onChange={ ( selectedValue ) =>
									onChange( {
										rowGap: `${ selectedValue }`,
									} )
								}
								initialPosition={ 0 }
								disabled={ props.rowGap?.type === 'readonly' }
								__next40pxDefaultSize
							/>
						</div>
					</div>
				) }
			</ToolsPanelItem>
			<ToolsPanelItem
				label={ _x( 'Floating', 'text', 'nelio-ab-testing' ) }
				hasValue={ () =>
					hasUserValue( props.float ) || hasUserValue( props.clear )
				}
				onSelect={ () => onChange( { float: 'none', clear: 'none' } ) }
				onDeselect={ () =>
					onChange( { float: undefined, clear: undefined } )
				}
				isShownByDefault={
					hasInheritedValue( props.float ) ||
					hasInheritedValue( props.clear )
				}
			>
				<div className="nab-components-floating-control">
					<SelectControl
						disabled={ 'readonly' === props.float?.type }
						label={ _x( 'Float', 'text', 'nelio-ab-testing' ) }
						value={ getPreviewValue( props.float ) }
						onChange={ ( selectedValue ) =>
							onChange( { float: selectedValue } )
						}
						options={ floatOptions }
					/>
					<SelectControl
						disabled={ 'readonly' === props.clear?.type }
						label={ _x( 'Clear', 'text', 'nelio-ab-testing' ) }
						value={ getPreviewValue( props.clear ) }
						onChange={ ( selectedValue ) =>
							onChange( { clear: selectedValue } )
						}
						options={ clearOptions }
					/>
				</div>
			</ToolsPanelItem>
			<ToolsPanelItem
				label={ _x( 'Visibility', 'text', 'nelio-ab-testing' ) }
				hasValue={ () => hasUserValue( props.visibility ) }
				onSelect={ () => onChange( { visibility: 'visible' } ) }
				onDeselect={ () => onChange( { visibility: undefined } ) }
				isShownByDefault={ hasInheritedValue( props.visibility ) }
			>
				<SelectControl
					disabled={ 'readonly' === props.visibility?.type }
					label={ _x( 'Visibility', 'text', 'nelio-ab-testing' ) }
					value={ getPreviewValue( props.visibility ) }
					onChange={ ( selectedValue ) =>
						onChange( { visibility: selectedValue } )
					}
					options={ [
						{
							value: 'visible',
							label: _x( 'Visible', 'text', 'nelio-ab-testing' ),
						},
						{
							value: 'hidden',
							label: _x( 'Hidden', 'text', 'nelio-ab-testing' ),
						},
						{
							value: 'collapse',
							label: _x( 'Collapse', 'text', 'nelio-ab-testing' ),
						},
					] }
				/>
			</ToolsPanelItem>
			<ToolsPanelItem
				label={ _x( 'Opacity', 'text', 'nelio-ab-testing' ) }
				hasValue={ () => hasUserValue( props.opacity ) }
				onSelect={ () => onChange( { opacity: '1' } ) }
				onDeselect={ () => onChange( { opacity: undefined } ) }
				isShownByDefault={ hasInheritedValue( props.opacity ) }
			>
				<RangeControl
					label={ _x( 'Opacity', 'text', 'nelio-ab-testing' ) }
					value={ Math.floor( opacityValue * 100 ) }
					initialPosition={ 100 }
					currentInput={ 100 }
					onChange={ ( next ) =>
						onChange( {
							opacity:
								next !== undefined
									? `${ parseFloat(
											( next / 100 ).toFixed( 2 )
									  ) }`
									: undefined,
						} )
					}
					step={ 1 }
					min={ 0 }
					max={ 100 }
					disabled={ 'readonly' === props.opacity?.type }
				/>
			</ToolsPanelItem>
			<ToolsPanelItem
				label={ _x( 'Cursor', 'text', 'nelio-ab-testing' ) }
				hasValue={ () =>
					hasUserValue( props.cursor ) ||
					hasUserValue( props.pointerEvents )
				}
				onSelect={ () =>
					onChange( { cursor: '1', pointerEvents: 'auto' } )
				}
				onDeselect={ () =>
					onChange( { cursor: undefined, pointerEvents: undefined } )
				}
				isShownByDefault={
					hasInheritedValue( props.cursor ) ||
					hasInheritedValue( props.pointerEvents )
				}
			>
				<div className="nab-components-cursor-control">
					<CustomSelectControl
						disabled={ 'readonly' === props.cursor?.type }
						label={ _x( 'Cursor', 'text', 'nelio-ab-testing' ) }
						value={ selectedCursorValue }
						onChange={ ( {
							selectedItem,
						}: {
							selectedItem: {
								key: string | undefined;
								name: string | undefined;
							};
						} ) => onChange( { cursor: selectedItem.key } ) }
						options={ cursorOptions }
						className={ clsx( 'block-editor-cursor-control', {
							'nab-components--disabled':
								'readonly' === props.cursor?.type,
						} ) }
					/>
					<ToggleControl
						label={ _x(
							'Pointer Events',
							'text',
							'nelio-ab-testing'
						) }
						disabled={ 'readonly' === props.pointerEvents?.type }
						checked={
							! props.pointerEvents?.value ||
							props.pointerEvents.value === 'auto'
						}
						onChange={ ( isToggled ) =>
							onChange( {
								pointerEvents: isToggled ? 'auto' : 'none',
							} )
						}
					/>
				</div>
			</ToolsPanelItem>
		</ToolsPanel>
	);
}
